/**
 * Schema Compare Navigation Service
 *
 * Handles navigation to schema compare tabs from context menus and other sources.
 * Supports opening new comparisons with pre-selected source/target.
 */

import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { getEngineFromConnectionId } from '../utils/engineUtils';
import { resolveDefaultSchemaCompareOptions } from '../store/schemaCompareOptionDefaultsStore';
import type {
  SchemaCompareTab,
  SchemaCompareEndpoint,
  SchemaCompareOptions,
} from '../../core/types/workspaceTypes';
import {
  folderCompareScopeFor,
  readFolderScopeMetadata,
} from '../utils/folderCompareScope';

/**
 * Find an existing schema compare tab that is open.
 * Returns the first schema-compare tab found, or undefined if none exists.
 */
export function findOpenSchemaCompareTab(): SchemaCompareTab | undefined {
  const { tabs } = useWorkspaceStore.getState();
  return tabs.find((tab): tab is SchemaCompareTab => tab.type === 'schema-compare');
}

/**
 * F07 — resolve the engine behind a connection id outside React, so a
 * navigation-created tab can read the per-engine starting options.
 */
function engineForConnection(connectionId: string | undefined): DatabaseEngineId | undefined {
  const { connections, activeConnections } = useConnectionStore.getState();
  return getEngineFromConnectionId(connectionId, connections, activeConnections);
}

/**
 * F07 — starting options for a comparison started by navigation (context menus,
 * "Apply schema to DB", …). These routes never showed the user an options
 * dialog, so before this they silently inherited
 * `createDefaultSchemaCompareContent()`'s hardcoded all-true / Deep baseline
 * through `addTab`'s `{...default, ...content}` merge — discarding whatever
 * the user had chosen on their previous compare.
 *
 * #89 — exported because the Apply-to-linked-DBs batch runs a comparison
 * WITHOUT opening a tab, and had reached for `createDefaultSchemaCompareContent()`
 * directly: the same hardcoded baseline this function exists to stop, so a user
 * who had turned Views off saw the batch count views the row's own "Open
 * compare" tab did not.
 */
export function startingSchemaCompareOptionsFor(
  connectionId: string | undefined,
): SchemaCompareOptions {
  return resolveDefaultSchemaCompareOptions(engineForConnection(connectionId));
}

/**
 * Open a new schema compare tab with the database pre-selected as source.
 */
export function openSchemaCompareAsSource(
  connectionId: string,
  database: string
): void {
  const { addTab } = useWorkspaceStore.getState();

  addTab({
    title: 'Schema Compare',
    type: 'schema-compare',
    content: {
      source: { connectionId, database },
      target: undefined,
      options: startingSchemaCompareOptionsFor(connectionId),
      hasCompared: false,
      isComparing: false,
      needsRefresh: false,
    },
  });
}

/**
 * Open a new schema compare tab with a Database Blueprint folder as source and the
 * given (connection, database) as target. Used by the "Apply schema to DB"
 * action on the Database Blueprint tab.
 *
 * Async since F07: the blueprint manifest has to be read before the tab is
 * created, because the tab auto-runs its comparison on open (`autoCompare`)
 * and an unscoped first run is exactly the destructive-diff case above.
 */
export async function openSchemaCompareWithFolderAsSource(
  folderPath: string,
  target: { connectionId: string; database: string; blueprintLinkLabel?: string },
): Promise<void> {
  const { addTab } = useWorkspaceStore.getState();

  // Read the blueprint's own scope. On failure we still open the tab (the
  // compare itself is read-only and the user reviews any script before
  // running it) but without a filter — same as the pre-F07 behaviour.
  //
  // The blueprint's own engine decides whether the folder can contain any
  // `Sequences/*.sql`. Falling back to the target connection's engine only
  // matters for a manifest with no usable link (hand-edited, or one written
  // before `engine` was recorded); at this point the target IS the link's
  // database, so the two agree wherever both exist.
  const scope = folderCompareScopeFor(
    await readFolderScopeMetadata(folderPath),
    target.blueprintLinkLabel,
    () => engineForConnection(target.connectionId),
  );

  const filterPart = scope.schemaFilter ? { schemaFilter: scope.schemaFilter } : {};

  addTab({
    title: 'Apply Schema to DB',
    type: 'schema-compare',
    content: {
      source: {
        sourceKind: 'folder',
        folderPath,
        blueprintLinkLabel: target.blueprintLinkLabel,
        ...filterPart,
      },
      target: {
        sourceKind: 'database',
        connectionId: target.connectionId,
        database: target.database,
        ...filterPart,
      },
      options: {
        ...startingSchemaCompareOptionsFor(target.connectionId),
        ...scope.typeCaps,
      },
      // Which of those flags the MANIFEST imposed, recorded so a later
      // "Change" cannot mistake them for the user's own preference and persist
      // them as this engine's default (W3.4/F07, fix round 3). Recorded
      // unconditionally (fix round 5) — an empty array is itself a real,
      // meaningful value ("this blueprint models every type, so nothing is
      // capped, but the tab IS folder-sourced"), and must not be conflated
      // with an absent marker, which re-enables the endpoint-kind fallback in
      // `pickInheritedCaps` / `narrowScopeCapKeys` for a tab that never needed
      // it in the first place.
      scopeCapKeys: scope.capKeys,
      hasCompared: false,
      isComparing: false,
      // The user explicitly opened this tab by clicking "Apply schema to
      // DB" — nothing was restored, so the "Session Restored / Run
      // Compare to refresh" banner would be misleading. Bug 1.
      needsRefresh: false,
      // Auto-run the comparison on open: the user already committed to the
      // compare by clicking "Apply schema to DB", so there's no reason to
      // make them click "Compare" again. SchemaCompareTab fires the compare
      // handler once when both endpoints are ready.
      autoCompare: true,
    },
  });
}

/**
 * Set a database as source in an existing schema compare tab.
 * If no tab exists, opens a new one with the source pre-selected.
 */
export function setSchemaCompareSource(
  connectionId: string,
  database: string
): void {
  const existingTab = findOpenSchemaCompareTab();

  if (existingTab) {
    const { updateTabContent, setActiveTab } = useWorkspaceStore.getState();
    // Update existing tab's source and navigate to it
    updateTabContent(existingTab.id, {
      source: { connectionId, database },
      needsRefresh: true,
    });
    setActiveTab(existingTab.id);
  } else {
    // Open new tab with source pre-selected
    openSchemaCompareAsSource(connectionId, database);
  }
}

/**
 * Set a database as target in an existing schema compare tab.
 * If no tab exists, opens a new one with the target pre-selected.
 */
export function setSchemaCompareTarget(
  connectionId: string,
  database: string
): void {
  const existingTab = findOpenSchemaCompareTab();
  const { addTab, updateTabContent, setActiveTab } = useWorkspaceStore.getState();

  if (existingTab) {
    // Update existing tab's target and navigate to it
    updateTabContent(existingTab.id, {
      target: { connectionId, database },
      needsRefresh: true,
    });
    setActiveTab(existingTab.id);
  } else {
    // Open new tab with target pre-selected
    const endpoint: SchemaCompareEndpoint = { connectionId, database };
    addTab({
      title: 'Schema Compare',
      type: 'schema-compare',
      content: {
        source: undefined,
        target: endpoint,
        options: startingSchemaCompareOptionsFor(connectionId),
        hasCompared: false,
        isComparing: false,
        needsRefresh: false,
      },
    });
  }
}
